import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "how page" }
const _hoisted_2 = { class: "hiw-wrap" }
const _hoisted_3 = { class: "overview-row" }
const _hoisted_4 = { class: "diagram-card" }
const _hoisted_5 = {
  class: "scenario-tabs",
  role: "tablist",
  "aria-label": "Scenario"
}
const _hoisted_6 = ["aria-selected", "onClick"]
const _hoisted_7 = { class: "pp" }
const _hoisted_8 = { class: "pp-cols" }
const _hoisted_9 = { class: "pp-col" }
const _hoisted_10 = { class: "pp-meta" }
const _hoisted_11 = { class: "pp-meta" }
const _hoisted_12 = { class: "pp-trigger" }
const _hoisted_13 = { class: "pp-out-title" }
const _hoisted_14 = { class: "pp-out-detail" }
const _hoisted_15 = { class: "scenario-note" }
const _hoisted_16 = { class: "hiw-section" }
const _hoisted_17 = { class: "subscript" }
const _hoisted_18 = {
  key: 0,
  class: "sub-body"
}
const _hoisted_19 = { class: "variant-tabs" }
const _hoisted_20 = {
  key: 0,
  class: "variant-note"
}
const _hoisted_21 = { class: "leaf-cols" }
const _hoisted_22 = { class: "leaf-col" }
const _hoisted_23 = { class: "leaf-kind mono" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = {
  key: 0,
  class: "script-detail"
}
const _hoisted_26 = { class: "leaf-col" }
const _hoisted_27 = { class: "leaf-kind mono" }
const _hoisted_28 = ["onClick"]
const _hoisted_29 = {
  key: 0,
  class: "script-detail"
}
const _hoisted_30 = { class: "hiw-footer" }

import { ref, computed } from 'vue'
import StepList from '@/components/StepList.vue'

// Expand state, keyed by leaf name (+ 'cond' for the shared win-condition).
type Scenario = 'happy' | 'nofund' | 'noreveal'
interface Step { ops: string[]; explain: string }

// ── Byte-accurate opcodes, disassembled from the real CoinflipEscrowScript
//    (packages/lib), grouped into steps with plain-language explanations. ──

// Coin win-determination condition (buildCoinflipConditionScript).
const VERIFY = 'OP_VERIFY'

// Reusable step fragments.
interface Leaf {
  key: string; name: string; group: 'collab' | 'exit'; cls: string
  kind: string; desc: string; steps: Step[]; payout?: 'player' | 'house'
}


export default /*@__PURE__*/_defineComponent({
  __name: 'HowItWorksView',
  setup(__props) {

const open = ref<Record<string, boolean>>({})
const toggle = (k: string) => { open.value = { ...open.value, [k]: !open.value[k] } }
const condVariant = ref<'coin' | 'odds'>('coin')

// ── At-a-glance scenario toggle ───────────────────────────────────
// The per-party escrow means each failure mode has a self-serve mitigation.
const scenarios: { id: Scenario; label: string }[] = [
  { id: 'happy', label: 'Happy path' },
  { id: 'nofund', label: "House doesn't fund" },
  { id: 'noreveal', label: 'Secret not revealed' },
]
const scenario = ref<Scenario>('happy')
const OUTCOMES: Record<Scenario, { kind: string; trigger: string; title: string; detail: string; note: string }> = {
  happy: {
    kind: 'win', trigger: 'you reveal → covenant settles',
    title: 'Winner takes the pot', detail: 'covenant pays the full pot on-chain',
    note: 'Both stakes are escrowed and you reveal — the Arkade-Script covenant settles the whole pot to the rightful winner. The winner signs nothing; the loser has no spend path.',
  },
  nofund: {
    kind: 'refund', trigger: 'timeout passes',
    title: 'You refund your stake', detail: 'CLTV self-refund — nothing at risk',
    note: "Because escrows are per-party, the house never touched your stake. If it never funds its own, there's no game — after the timeout you reclaim your stake with just your key. A no-show can't cost you.",
  },
  noreveal: {
    kind: 'forfeit', trigger: 'you revealed → deadline passes',
    title: 'You take the whole pot', detail: 'forfeit path (CLTV) — the staller loses',
    note: "You revealed but the operator won't settle. After the deadline the forfeit leaf lets you sweep BOTH stakes to yourself — stalling costs the house everything.",
  },
}
const outcome = computed(() => OUTCOMES[scenario.value])

const WIN_COIN: Step[] = [
  { ops: ['OP_2DUP', 'OP_SHA256', '<playerHash>', 'OP_EQUALVERIFY', 'OP_SHA256', '<houseHash>', 'OP_EQUALVERIFY'],
    explain: 'Verify both reveals: SHA-256 each secret and require it to equal the hash committed at /play. A wrong secret fails here.' },
  { ops: ['OP_SIZE', 'OP_DUP', 'OP_16', 'OP_EQUAL', 'OP_SWAP', 'OP_15', 'OP_EQUAL', 'OP_BOOLOR', 'OP_NOTIF', 'OP_2DROP', 'OP_0', 'OP_ELSE'],
    explain: "Validate the player's secret length — it must be 15 (heads) or 16 (tails). If neither, push 0 → house wins (a bad length loses)." },
  { ops: ['OP_SWAP', 'OP_SIZE', 'OP_DUP', 'OP_16', 'OP_EQUAL', 'OP_SWAP', 'OP_15', 'OP_EQUAL', 'OP_BOOLOR', 'OP_NOTIF', 'OP_2DROP', 'OP_1', 'OP_ELSE'],
    explain: "Same length check on the house's secret. If the house's length is invalid, push 1 → player wins." },
  { ops: ['OP_SIZE', 'OP_SWAP', 'OP_DROP', 'OP_SWAP', 'OP_SIZE', 'OP_SWAP', 'OP_DROP', 'OP_EQUAL', 'OP_ENDIF', 'OP_ENDIF'],
    explain: 'Both lengths valid → compare them. Equal → 1 (player wins); different → 0 (house wins). That is the coin flip.' },
]

// Variable-odds condition (buildVariableOddsConditionScript), shown for n=6, lo=0, target=3.
const WIN_ODDS: Step[] = [
  { ops: ['OP_2DUP', 'OP_SHA256', '<playerHash>', 'OP_EQUALVERIFY', 'OP_SHA256', '<houseHash>', 'OP_EQUALVERIFY'],
    explain: 'Verify both reveals against their committed hashes — identical to the coin.' },
  { ops: ['OP_SIZE', 'OP_DUP', 'OP_16', 'OP_GREATERTHANOREQUAL', 'OP_SWAP', '22', 'OP_LESSTHAN', 'OP_BOOLAND', 'OP_NOTIF', 'OP_2DROP', 'OP_0', 'OP_ELSE'],
    explain: "Player's length must be in [16, 16+n) — here [16, 22). The length minus 16 is the player's digit in [0, n). Out of range → 0, house wins." },
  { ops: ['OP_SWAP', 'OP_SIZE', 'OP_DUP', 'OP_16', 'OP_GREATERTHANOREQUAL', 'OP_SWAP', '22', 'OP_LESSTHAN', 'OP_BOOLAND', 'OP_NOTIF', 'OP_2DROP', 'OP_1', 'OP_ELSE'],
    explain: "Same range check on the house's secret. House out of range → 1, player wins." },
  { ops: ['OP_SIZE', 'OP_NIP', 'OP_16', 'OP_SUB', 'OP_SWAP', 'OP_SIZE', 'OP_NIP', 'OP_16', 'OP_SUB', 'OP_ADD'],
    explain: 'Decode both digits (digit = length − 16) and add them: sum = digit_house + digit_player, which lands in [0, 2n−2].' },
  { ops: ['OP_DUP', 'OP_6', 'OP_GREATERTHANOREQUAL', 'OP_IF', 'OP_6', 'OP_SUB', 'OP_ENDIF'],
    explain: 'roll = sum mod n. OP_MOD is disabled in Script, but since sum < 2n one conditional subtract does it: if sum ≥ n (6), subtract n.' },
  { ops: ['OP_DUP', 'OP_0', 'OP_GREATERTHANOREQUAL', 'OP_SWAP', 'OP_3', 'OP_LESSTHAN', 'OP_BOOLAND', 'OP_ENDIF', 'OP_ENDIF'],
    explain: 'Player wins iff lo ≤ roll < target — here 0 ≤ roll < 3. Pushes 1 (player) or 0 (house). Win probability = (target − lo) / n.' },
]

// The arkade covenant (covenants.atomicSweep) the emulator runs before signing <emu✦>.
function arkadeSteps(payout: 'player' | 'house'): Step[] {
  const payKey = payout === 'player' ? '<playerPayoutKey>' : '<housePayoutKey>'
  return [
    { ops: ['OP_INSPECTINPUTVALUE', '<otherEscrowStake>', 'OP_EQUALVERIFY'],
      explain: 'Atomicity: the spending tx must also include the OTHER escrow as an input, its value pinned. Neither escrow can be swept on its own.' },
    { ops: ['OP_DUP', 'OP_INSPECTOUTPUTSCRIPTPUBKEY', 'OP_1', 'OP_EQUALVERIFY', payKey, 'OP_EQUALVERIFY'],
      explain: "Destination is forced: output #1's scriptPubKey must be exactly the winner's payout key (P2TR)." },
    { ops: ['OP_INSPECTOUTPUTVALUE', '<fullPot>', 'OP_EQUAL'],
      explain: 'Amount is forced: that output must carry the full pot. The emulator co-signs <emu✦> only if all three checks pass.' },
  ]
}

const COND: string[] = ['‹win condition›']
const winStep = (negate = false): Step => ({
  ops: negate ? [...COND, 'OP_NOT', VERIFY] : [...COND, VERIFY],
  explain: negate
    ? 'Evaluate the win condition and negate it — require the HOUSE won.'
    : 'Evaluate the win condition — require the PLAYER won (true).',
})
const cltvStep: Step = {
  ops: ['<finalExpiration>', 'OP_CHECKLOCKTIMEVERIFY', 'OP_DROP'],
  explain: 'Absolute timelock: this path is unspendable until finalExpiration.',
}
const csvStep: Step = {
  ops: ['<exitDelay>', 'OP_CHECKSEQUENCEVERIFY', 'OP_DROP'],
  explain: 'Relative timelock (CSV): spendable alone after exitDelay — survives an operator outage.',
}

const LEAVES: Leaf[] = [
  {
    key: 'playerWinCovenant', name: 'playerWinCovenant', group: 'collab', cls: 'text-green',
    kind: 'ConditionMultisig[server, emu✦]',
    desc: 'Player won → the operator settles the full pot to the player. The player signs nothing.',
    payout: 'player',
    steps: [winStep(false),
      { ops: ['<serverKey>', 'OP_CHECKSIGVERIFY', '<emu✦>', 'OP_CHECKSIG'],
        explain: '2-of-2: the operator signs, and <emu✦> is the emulator key tweaked by this leaf’s arkade covenant — signed only after the covenant (below) passes. So the pot can only land where the covenant says.' }],
  },
  {
    key: 'creatorWinCovenant', name: 'creatorWinCovenant', group: 'collab', cls: 'text-green',
    kind: 'ConditionMultisig[server, emu✦]',
    desc: 'House won (win condition negated) → the pot settles to the house.',
    payout: 'house',
    steps: [winStep(true),
      { ops: ['<serverKey>', 'OP_CHECKSIGVERIFY', '<emu✦>', 'OP_CHECKSIG'],
        explain: '2-of-2 [operator, emu✦]: same covenant binding, paying the house key.' }],
  },
  {
    key: 'playerForfeit', name: 'playerForfeit', group: 'collab', cls: 'text-gold',
    kind: 'CLTVMultisig[player, server, emu✦]',
    desc: 'If the operator stalls after you revealed, you sweep the entire pot after the deadline.',
    payout: 'player',
    steps: [cltvStep,
      { ops: ['<playerKey>', 'OP_CHECKSIGVERIFY', '<serverKey>', 'OP_CHECKSIGVERIFY', '<emu✦>', 'OP_CHECKSIG'],
        explain: '3-of-3 [player, operator, emu✦]: after the deadline the player sweeps the full pot — the covenant forces the payout to the player. This is the penalty for a stalling house.' }],
  },
  {
    key: 'refund', name: 'refund', group: 'collab', cls: 'text-blue',
    kind: 'CLTVMultisig[funder, server]',
    desc: 'If the game never resolved, each funder reclaims their own stake after the deadline.',
    steps: [cltvStep,
      { ops: ['<funderKey>', 'OP_CHECKSIGVERIFY', '<serverKey>', 'OP_CHECKSIG'],
        explain: '2-of-2 [funder, operator]: the funder reclaims only their own stake — no covenant, no payout binding. No winner, no loss.' }],
  },
  {
    key: 'playerWinExit', name: 'playerWinExit', group: 'exit', cls: '',
    kind: 'ConditionCSVMultisig[player, emu✦]',
    desc: 'The player-win payout, spendable alone after a CSV delay — survives an operator outage.',
    payout: 'player',
    steps: [winStep(false), csvStep,
      { ops: ['<playerKey>', 'OP_CHECKSIGVERIFY', '<emu✦>', 'OP_CHECKSIG'],
        explain: '2-of-2 [player, emu✦]: no operator signature needed; the emulator still enforces the covenant payout.' }],
  },
  {
    key: 'creatorWinExit', name: 'creatorWinExit', group: 'exit', cls: '',
    kind: 'ConditionCSVMultisig[creator, emu✦]',
    desc: 'The house win path, as a self-spendable CSV mirror.',
    payout: 'house',
    steps: [winStep(true), csvStep,
      { ops: ['<houseKey>', 'OP_CHECKSIGVERIFY', '<emu✦>', 'OP_CHECKSIG'],
        explain: '2-of-2 [house, emu✦]: the house exits its win without the operator.' }],
  },
  {
    key: 'playerForfeitExit', name: 'playerForfeitExit', group: 'exit', cls: '',
    kind: 'ConditionCSVMultisig[player, emu✦]',
    desc: 'The forfeit sweep as a CSV mirror, gated by a hash-check on your revealed secret.',
    payout: 'player',
    steps: [
      { ops: ['OP_SHA256', '<playerHash>', 'OP_EQUAL', VERIFY],
        explain: 'Hash-lock: prove you know the secret behind your commitment (you revealed).' },
      csvStep,
      { ops: ['<playerKey>', 'OP_CHECKSIGVERIFY', '<emu✦>', 'OP_CHECKSIG'],
        explain: '2-of-2 [player, emu✦]: sweep the pot to the player without the operator.' }],
  },
  {
    key: 'refundExit', name: 'refundExit', group: 'exit', cls: 'text-blue',
    kind: 'CSVMultisig[funder]',
    desc: 'Last resort — no operator, no emulator. The funder reclaims their stake after the CSV delay.',
    steps: [csvStep,
      { ops: ['<funderKey>', 'OP_CHECKSIG'],
        explain: 'Just the funder’s signature — no operator, no emulator. The final fallback if everything else is down.' }],
  },
]

const collabLeaves = computed(() => LEAVES.filter((l) => l.group === 'collab'))
const exitLeaves = computed(() => LEAVES.filter((l) => l.group === 'exit'))

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_router_link, {
        to: "/",
        class: "back-link"
      }, {
        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
          _createTextVNode("« back to play", -1)
        ]))]),
        _: 1
      }),
      _cache[26] || (_cache[26] = _createElementVNode("header", { class: "hiw-header" }, [
        _createElementVNode("h1", null, "How It Works"),
        _createElementVNode("p", { class: "lede" }, [
          _createTextVNode(" A "),
          _createElementVNode("strong", { class: "text-gold" }, "trustless"),
          _createTextVNode(" coinflip: the fairness and the payout are enforced by "),
          _createElementVNode("strong", null, "Bitcoin script"),
          _createTextVNode(" and an "),
          _createElementVNode("strong", null, "Arkade Script"),
          _createTextVNode(" covenant — not by trusting the operator. Everything below is taken directly from this repo's code; expand any spend path to see its exact opcodes, step by step. ")
        ])
      ], -1)),
      _createElementVNode("section", _hoisted_3, [
        _cache[8] || (_cache[8] = _createStaticVNode("<div class=\"tldr casino-card\" data-v-b969f3c6><h2 class=\"tldr-title\" data-v-b969f3c6>The 30-second version</h2><ol class=\"tldr-steps\" data-v-b969f3c6><li data-v-b969f3c6>Both sides <strong data-v-b969f3c6>commit</strong> to a hidden secret (only its hash is shared).</li><li data-v-b969f3c6>Each side <strong data-v-b969f3c6>escrows</strong> its own stake into its <strong data-v-b969f3c6>own</strong> per-party escrow — neither can touch the other&#39;s.</li><li data-v-b969f3c6>You <strong data-v-b969f3c6>reveal</strong> your secret. The two secrets together decide the winner.</li><li data-v-b969f3c6>An <strong data-v-b969f3c6>Arkade Script covenant</strong> pays the full pot to the winner — the winner never signs, and the loser has no spend path.</li><li data-v-b969f3c6>If the operator goes silent, the contract still lets you <strong data-v-b969f3c6>reclaim or sweep</strong> on your own.</li></ol></div>", 1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(scenarios, (s) => {
              return _createElementVNode("button", {
                key: s.id,
                type: "button",
                role: "tab",
                "aria-selected": scenario.value === s.id,
                class: _normalizeClass(['scenario-tab', { active: scenario.value === s.id }]),
                onClick: ($event: any) => (scenario.value = s.id)
              }, _toDisplayString(s.label), 11, _hoisted_6)
            }), 64))
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _cache[6] || (_cache[6] = _createStaticVNode("<div class=\"pp-col\" data-v-b969f3c6><div class=\"pp-box you\" data-v-b969f3c6><span class=\"pp-name\" data-v-b969f3c6>YOU</span><span class=\"pp-meta\" data-v-b969f3c6>commit + stake</span></div><span class=\"pp-arr\" data-v-b969f3c6>↓</span><div class=\"pp-box escrow filled\" data-v-b969f3c6><span class=\"pp-name\" data-v-b969f3c6>your escrow</span><span class=\"pp-meta\" data-v-b969f3c6>staked</span></div></div>", 1)),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", {
                  class: _normalizeClass(["pp-box house", { ghost: scenario.value === 'nofund' }])
                }, [
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "pp-name" }, "HOUSE", -1)),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(scenario.value === 'nofund' ? 'no-show' : 'commit + stake'), 1)
                ], 2),
                _createElementVNode("span", {
                  class: _normalizeClass(["pp-arr", { ghost: scenario.value === 'nofund' }])
                }, "↓", 2),
                _createElementVNode("div", {
                  class: _normalizeClass(["pp-box escrow", scenario.value === 'nofund' ? 'empty' : 'filled'])
                }, [
                  _cache[5] || (_cache[5] = _createElementVNode("span", { class: "pp-name" }, "house escrow", -1)),
                  _createElementVNode("span", _hoisted_11, _toDisplayString(scenario.value === 'nofund' ? 'never funded' : 'staked'), 1)
                ], 2)
              ])
            ]),
            _createElementVNode("div", _hoisted_12, _toDisplayString(outcome.value.trigger), 1),
            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "pp-merge" }, "↓", -1)),
            _createElementVNode("div", {
              class: _normalizeClass(["pp-outcome", outcome.value.kind])
            }, [
              _createElementVNode("span", _hoisted_13, _toDisplayString(outcome.value.title), 1),
              _createElementVNode("span", _hoisted_14, _toDisplayString(outcome.value.detail), 1)
            ], 2)
          ]),
          _createElementVNode("p", _hoisted_15, _toDisplayString(outcome.value.note), 1)
        ])
      ]),
      _cache[27] || (_cache[27] = _createStaticVNode("<section class=\"hiw-section\" data-v-b969f3c6><h2 data-v-b969f3c6>The building blocks</h2><div class=\"layer-grid\" data-v-b969f3c6><article class=\"layer-card\" data-v-b969f3c6><div class=\"layer-badge arkade\" data-v-b969f3c6>ARKADE</div><h3 data-v-b969f3c6>Arkade — the execution layer</h3><p data-v-b969f3c6> Arkade is a <strong data-v-b969f3c6>programmable Bitcoin execution layer</strong>. Coins are held as <strong data-v-b969f3c6>VTXOs</strong> (Virtual Transaction Outputs) — self-custodial, off-chain Bitcoin coins that move <strong data-v-b969f3c6>instantly at near-zero fees</strong>, with no changes to Bitcoin required. The coinflip stakes are VTXOs. </p><p data-v-b969f3c6> Two of its properties make this game possible: a VTXO can be locked by <strong data-v-b969f3c6>any valid Tapscript</strong> (so the escrow&#39;s spend paths below are enforceable), and you keep <strong data-v-b969f3c6>unilateral exit</strong> — funds are always withdrawable on-chain without the operator&#39;s cooperation. More at <a href=\"https://docs.arkadeos.com\" target=\"_blank\" rel=\"noopener\" data-v-b969f3c6>docs.arkadeos.com</a>. </p></article><article class=\"layer-card\" data-v-b969f3c6><div class=\"layer-badge script\" data-v-b969f3c6>ARKADE SCRIPT</div><h3 data-v-b969f3c6>Arkade Script — the covenant</h3><p data-v-b969f3c6> Plain Bitcoin script can&#39;t say <em data-v-b969f3c6>&quot;this may only be spent to address X for exactly amount Y.&quot;</em> That rule — a <strong data-v-b969f3c6>covenant</strong> — is what makes the trustless payout possible. </p><p data-v-b969f3c6> In the code it works through an <strong data-v-b969f3c6>emulator</strong> co-signer. A covenant leaf is an ordinary multisig whose emulator pubkey is <em data-v-b969f3c6>tweaked</em> by <span class=\"mono\" data-v-b969f3c6>pubkey + tagged_hash(&quot;ArkScriptHash&quot;, arkadeScript)·G</span>. The emulator produces that tweaked signature <strong data-v-b969f3c6>only after running the arkade script</strong> — so a signature on the <span class=\"mono\" data-v-b969f3c6>&lt;emu✦&gt;</span> slot <em data-v-b969f3c6>is</em> the proof the covenant held. The script bytes travel at spend time in the <span class=\"mono\" data-v-b969f3c6>EmulatorPacket</span>. </p></article></div></section><section class=\"hiw-section\" data-v-b969f3c6><h2 data-v-b969f3c6>The commit–reveal coin</h2><p class=\"section-intro\" data-v-b969f3c6> The randomness comes from two secrets neither side can see in advance. The trick: the <strong data-v-b969f3c6>length</strong> of the secret encodes the choice. </p><div class=\"cr-grid\" data-v-b969f3c6><div class=\"cr-card\" data-v-b969f3c6><div class=\"cr-step\" data-v-b969f3c6>1 · Commit</div><p data-v-b969f3c6>Each side shares only the <strong data-v-b969f3c6>SHA-256 hash</strong> of a random secret — hiding both the bytes and the length, so neither can react to the other&#39;s choice.</p></div><div class=\"cr-card\" data-v-b969f3c6><div class=\"cr-step\" data-v-b969f3c6>2 · Encode</div><p data-v-b969f3c6>Coin: <span class=\"chip\" data-v-b969f3c6>15 bytes = Heads</span> <span class=\"chip\" data-v-b969f3c6>16 bytes = Tails</span>. Variable-odds: length is <span class=\"mono\" data-v-b969f3c6>16 + digit</span>, <span class=\"mono\" data-v-b969f3c6>digit ∈ [0, n)</span>.</p></div><div class=\"cr-card\" data-v-b969f3c6><div class=\"cr-step\" data-v-b969f3c6>3 · Reveal</div><p data-v-b969f3c6>Coin: you win when your secret&#39;s length <strong data-v-b969f3c6>matches</strong> the house&#39;s. Odds: <span class=\"mono\" data-v-b969f3c6>roll = (d_house + d_player) mod n</span>; you win when <span class=\"mono\" data-v-b969f3c6>lo ≤ roll &lt; target</span>.</p></div></div><p class=\"note\" data-v-b969f3c6><span class=\"note-tag\" data-v-b969f3c6>Fair RNG</span> The coin side and the odds digit are drawn from a <strong data-v-b969f3c6>CSPRNG</strong>; the committed hash binds each side to its choice <em data-v-b969f3c6>before</em> any reveal. The <span class=\"mono\" data-v-b969f3c6>‹win condition›</span> tapscript below verifies exactly this on-chain. </p></section>", 2)),
      _createElementVNode("section", _hoisted_16, [
        _cache[21] || (_cache[21] = _createElementVNode("h2", null, "The escrow contract — 8 spend paths", -1)),
        _cache[22] || (_cache[22] = _createElementVNode("p", { class: "section-intro" }, [
          _createTextVNode(" Stakes go into a Taproot contract ("),
          _createElementVNode("span", { class: "mono" }, "CoinflipEscrowScript"),
          _createTextVNode("). It's "),
          _createElementVNode("strong", null, "per-party"),
          _createTextVNode(" — house funds the house escrow, you fund the player escrow — so neither side can abort and steal the other's stake. Each escrow has eight tapscript leaves: four "),
          _createElementVNode("strong", null, "collaborative"),
          _createTextVNode(" covenant paths and four "),
          _createElementVNode("strong", null, "unilateral exit"),
          _createTextVNode(" mirrors. Click "),
          _createElementVNode("span", { class: "mono" }, "show script ▸"),
          _createTextVNode(" on any leaf for its opcodes, explained step by step. ")
        ], -1)),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("button", {
            class: "sub-toggle",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (toggle('cond')))
          }, [
            _createElementVNode("span", {
              class: _normalizeClass(["caret", { open: open.value.cond }])
            }, "▸", 2),
            _cache[9] || (_cache[9] = _createElementVNode("span", { class: "mono" }, "‹win condition›", -1)),
            _cache[10] || (_cache[10] = _createElementVNode("span", { class: "sub-hint" }, "the on-chain script that decides the winner", -1))
          ]),
          (open.value.cond)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("button", {
                    class: _normalizeClass(['variant-tab', { active: condVariant.value === 'coin' }]),
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (condVariant.value = 'coin'))
                  }, "Coin (50/50)", 2),
                  _createElementVNode("button", {
                    class: _normalizeClass(['variant-tab', { active: condVariant.value === 'odds' }]),
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (condVariant.value = 'odds'))
                  }, "Variable odds", 2)
                ]),
                (condVariant.value === 'odds')
                  ? (_openBlock(), _createElementBlock("p", _hoisted_20, [...(_cache[11] || (_cache[11] = [
                      _createTextVNode(" Example shown: ", -1),
                      _createElementVNode("span", { class: "mono" }, "n = 6, lo = 0, target = 3", -1),
                      _createTextVNode(" — a \"roll under 3 on a d6\" bet (player wins ~50%). Other skins just change ", -1),
                      _createElementVNode("span", { class: "mono" }, "n / lo / target", -1),
                      _createTextVNode(". ", -1)
                    ]))]))
                  : _createCommentVNode("", true),
                _createVNode(StepList, {
                  steps: condVariant.value === 'coin' ? WIN_COIN : WIN_ODDS
                }, null, 8, ["steps"]),
                _cache[12] || (_cache[12] = _createStaticVNode("<p class=\"ops-note\" data-v-b969f3c6>Result: <span class=\"mono\" data-v-b969f3c6>1</span> → player wins, <span class=\"mono\" data-v-b969f3c6>0</span> → house wins. <span class=\"mono\" data-v-b969f3c6>creatorWin*</span> leaves wrap this in <span class=\"mono\" data-v-b969f3c6>OP_NOT</span>. An invalid secret length <em data-v-b969f3c6>loses</em> (it can&#39;t void the game).</p>", 1))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_21, [
          _createElementVNode("div", _hoisted_22, [
            _cache[16] || (_cache[16] = _createElementVNode("div", { class: "leaf-col-head collab" }, "Collaborative (operator + emulator)", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(collabLeaves.value, (leaf) => {
              return (_openBlock(), _createElementBlock("div", {
                key: leaf.key,
                class: "leaf"
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["leaf-name", leaf.cls])
                }, _toDisplayString(leaf.name), 3),
                _createElementVNode("div", _hoisted_23, _toDisplayString(leaf.kind), 1),
                _createElementVNode("p", null, _toDisplayString(leaf.desc), 1),
                _createElementVNode("button", {
                  class: "show-script",
                  onClick: ($event: any) => (toggle(leaf.key))
                }, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["caret", { open: open.value[leaf.key] }])
                  }, "▸", 2),
                  _cache[13] || (_cache[13] = _createTextVNode(" show script ", -1))
                ], 8, _hoisted_24),
                (open.value[leaf.key])
                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                      _cache[15] || (_cache[15] = _createElementVNode("div", { class: "script-label" }, "Tapscript — spend path", -1)),
                      _createVNode(StepList, {
                        steps: leaf.steps
                      }, null, 8, ["steps"]),
                      (leaf.payout)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _cache[14] || (_cache[14] = _createElementVNode("div", { class: "script-label arkade-label" }, [
                              _createTextVNode("Arkade covenant — run by the emulator for "),
                              _createElementVNode("span", { class: "mono" }, "<emu✦>")
                            ], -1)),
                            _createVNode(StepList, {
                              steps: arkadeSteps(leaf.payout)
                            }, null, 8, ["steps"])
                          ], 64))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_26, [
            _cache[20] || (_cache[20] = _createElementVNode("div", { class: "leaf-col-head exit" }, "Unilateral exit (no operator needed)", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(exitLeaves.value, (leaf) => {
              return (_openBlock(), _createElementBlock("div", {
                key: leaf.key,
                class: "leaf"
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["leaf-name", leaf.cls])
                }, _toDisplayString(leaf.name), 3),
                _createElementVNode("div", _hoisted_27, _toDisplayString(leaf.kind), 1),
                _createElementVNode("p", null, _toDisplayString(leaf.desc), 1),
                _createElementVNode("button", {
                  class: "show-script",
                  onClick: ($event: any) => (toggle(leaf.key))
                }, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["caret", { open: open.value[leaf.key] }])
                  }, "▸", 2),
                  _cache[17] || (_cache[17] = _createTextVNode(" show script ", -1))
                ], 8, _hoisted_28),
                (open.value[leaf.key])
                  ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                      _cache[19] || (_cache[19] = _createElementVNode("div", { class: "script-label" }, "Tapscript — spend path", -1)),
                      _createVNode(StepList, {
                        steps: leaf.steps
                      }, null, 8, ["steps"]),
                      (leaf.payout)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _cache[18] || (_cache[18] = _createElementVNode("div", { class: "script-label arkade-label" }, [
                              _createTextVNode("Arkade covenant — run by the emulator for "),
                              _createElementVNode("span", { class: "mono" }, "<emu✦>")
                            ], -1)),
                            _createVNode(StepList, {
                              steps: arkadeSteps(leaf.payout)
                            }, null, 8, ["steps"])
                          ], 64))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            }), 128))
          ])
        ]),
        _cache[23] || (_cache[23] = _createElementVNode("p", { class: "legend mono" }, [
          _createElementVNode("span", { class: "tok tok-var" }, "<emu✦>"),
          _createTextVNode(" emulator key tweaked by that leaf's arkade script · "),
          _createElementVNode("span", { class: "tok tok-arkade" }, "OP_INSPECT…"),
          _createTextVNode(" Arkade-extension opcodes (tx introspection) · ‹win condition› expands above ")
        ], -1))
      ]),
      _cache[28] || (_cache[28] = _createStaticVNode("<section class=\"hiw-section\" data-v-b969f3c6><h2 data-v-b969f3c6>A game, end to end</h2><div class=\"flow\" data-v-b969f3c6><div class=\"flow-step\" data-v-b969f3c6><div class=\"flow-num\" data-v-b969f3c6>1</div><div class=\"flow-body\" data-v-b969f3c6><h4 data-v-b969f3c6>Play &amp; commit</h4><p data-v-b969f3c6>You commit your secret&#39;s hash; the house commits its own. The server returns a shared escrow address derived from both commitments.</p></div></div><div class=\"flow-step\" data-v-b969f3c6><div class=\"flow-num\" data-v-b969f3c6>2</div><div class=\"flow-body\" data-v-b969f3c6><h4 data-v-b969f3c6>Escrow the stakes</h4><p data-v-b969f3c6>You fund the player escrow, the house funds the house escrow — two single-party transactions into the same contract.</p></div></div><div class=\"flow-step\" data-v-b969f3c6><div class=\"flow-num\" data-v-b969f3c6>3</div><div class=\"flow-body\" data-v-b969f3c6><h4 data-v-b969f3c6>Reveal</h4><p data-v-b969f3c6>You send your secret to the server. Combined with the house secret, it deterministically decides the winner.</p></div></div><div class=\"flow-step\" data-v-b969f3c6><div class=\"flow-num\" data-v-b969f3c6>4</div><div class=\"flow-body\" data-v-b969f3c6><h4 data-v-b969f3c6>Covenant settlement</h4><p data-v-b969f3c6>The server builds one atomic sweep of <em data-v-b969f3c6>both</em> escrows to the winner&#39;s address and hands it to the emulator. The emulator runs the arkade script, confirms the payout matches, co-signs the <span class=\"mono\" data-v-b969f3c6>&lt;emu✦&gt;</span> slot, and forwards it on. <strong data-v-b969f3c6>The winner signs nothing.</strong></p></div></div><div class=\"flow-step recovery\" data-v-b969f3c6><div class=\"flow-num\" data-v-b969f3c6>!</div><div class=\"flow-body\" data-v-b969f3c6><h4 data-v-b969f3c6>If the operator goes dark</h4><p data-v-b969f3c6>Revealed but unpaid? Sweep the whole pot via <span class=\"mono\" data-v-b969f3c6>playerForfeit</span> after the deadline. Never revealed? <span class=\"mono\" data-v-b969f3c6>refund</span>. Operator censoring? Take a unilateral <span class=\"mono\" data-v-b969f3c6>*Exit</span> after the CSV delay.</p></div></div></div></section><section class=\"hiw-section trust\" data-v-b969f3c6><h2 data-v-b969f3c6>What you have to trust</h2><ul class=\"trust-list\" data-v-b969f3c6><li data-v-b969f3c6><span class=\"tick\" data-v-b969f3c6>✓</span><span class=\"t\" data-v-b969f3c6><strong data-v-b969f3c6>Not the outcome.</strong> It&#39;s fixed by the committed hashes before any reveal.</span></li><li data-v-b969f3c6><span class=\"tick\" data-v-b969f3c6>✓</span><span class=\"t\" data-v-b969f3c6><strong data-v-b969f3c6>Not the payout.</strong> The covenant only authorizes the full pot to the rightful winner&#39;s key; the loser has no spendable leaf.</span></li><li data-v-b969f3c6><span class=\"tick\" data-v-b969f3c6>✓</span><span class=\"t\" data-v-b969f3c6><strong data-v-b969f3c6>Not the operator&#39;s uptime.</strong> The forfeit, refund, and exit leaves recover your funds without it.</span></li><li data-v-b969f3c6><span class=\"cross\" data-v-b969f3c6>·</span><span class=\"t\" data-v-b969f3c6><strong data-v-b969f3c6>You do rely on</strong> the emulator running the published arkade script honestly for the <em data-v-b969f3c6>fast</em> path — and on Arkade itself (<a href=\"https://docs.arkadeos.com\" target=\"_blank\" rel=\"noopener\" data-v-b969f3c6>docs</a>). If the emulator misbehaves or vanishes, <span class=\"mono\" data-v-b969f3c6>refundExit</span> still returns your stake with no operator and no emulator.</span></li></ul></section>", 2)),
      _createElementVNode("footer", _hoisted_30, [
        _createVNode(_component_router_link, {
          to: "/",
          class: "btn-gold"
        }, {
          default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
            _createTextVNode("Start playing", -1)
          ]))]),
          _: 1
        }),
        _cache[25] || (_cache[25] = _createElementVNode("span", { class: "footer-cite text-muted" }, [
          _createTextVNode("Secret-length coin scheme inspired by "),
          _createElementVNode("a", {
            href: "https://arxiv.org/pdf/1612.05390v3",
            target: "_blank",
            rel: "noopener"
          }, "arxiv.org/pdf/1612.05390v3")
        ], -1))
      ])
    ])
  ]))
}
}

})